// Third Party
import React, { useState, useMemo } from 'react';

// Components
import { Tooltip, DisclaimerTooltip } from '@/views/components/tooltips';
import ModalV2 from '@/views/components/modals/ModalV2';
import Icon from '@/views/components/common/Icon';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { DISCLAIMER_KEYS } from '@/utils/disclaimers/constants';
import ComparisonItem from '@/types/comparison-item';

type Props = {
  item: ComparisonItem;
  showLongerHistory: boolean;
  shortHistoryDates: string[];
};
const HEADER_DISCLAIMERS = {
  [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_NO_NAV_DATA, DISCLAIMER_KEYS.FUND_NAV_CLOSED, DISCLAIMER_KEYS.USING_ROLLING_BACKFILL],
  [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_NAV_DATA, DISCLAIMER_KEYS.STOCK_NAV_CLOSED, DISCLAIMER_KEYS.USING_ROLLING_BACKFILL],
  [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_CLOSED_HOLDINGS_NAV, DISCLAIMER_KEYS.USING_ROLLING_BACKFILL],
  [COMPARISON_ITEM_TYPES.BENCHMARK]: [],
  [COMPARISON_ITEM_TYPES.GROUP]: [DISCLAIMER_KEYS.USING_ROLLING_BACKFILL],
  [COMPARISON_ITEM_TYPES.ALT]: [DISCLAIMER_KEYS.USING_ROLLING_BACKFILL],
};

export const ComparisonDisclaimer: React.FC<Props> = ({ item, showLongerHistory, shortHistoryDates }) => {
  const [showModal, setShowModal] = useState(false);

  const disclaimerInfo = useMemo(() => {
    const { disclaimers } = item;
    if (!disclaimers) return null;

    const headerDisclaimerKeys = HEADER_DISCLAIMERS[item.type] || [];
    const availableHeaderDisclaimers = headerDisclaimerKeys.filter(key => !!disclaimers[key]);

    const hasRollingBackfill = !!disclaimers[DISCLAIMER_KEYS.USING_ROLLING_BACKFILL];
    const otherDisclaimers = availableHeaderDisclaimers.filter(key => key !== DISCLAIMER_KEYS.USING_ROLLING_BACKFILL);
    const shouldRender = availableHeaderDisclaimers.length > 0 || hasRollingBackfill || showLongerHistory;

    return {
      disclaimers,
      hasRollingBackfill,
      otherDisclaimers,
      shouldRender,
    };
  }, [item._id, item.type, item.disclaimers, showLongerHistory]);

  if (!disclaimerInfo?.shouldRender) return null;

  const { disclaimers, hasRollingBackfill, otherDisclaimers } = disclaimerInfo;

  const modalBody = (
    <div className="comparison-disclaimer-modal-body">
      <h3>{formatMessage('Disclaimers.UsingRollingBackfill.Title')}</h3>
      <p>{formatMessage('Disclaimers.UsingRollingBackfill.Message')}</p>
    </div>
  );

  return (
    <div className="comparison-card-disclaimer">
      {showLongerHistory && (
        <Tooltip
          title={formatMessage('Comparison.LongHistoryTooltip.Title')}
          message={formatMessage('Comparison.LongHistoryTooltip.Message')}
          list={shortHistoryDates}
          place="right"
        >
          <span>
            <Icon type="info" size={12} boxPad={24} />
            {formatMessage('Comparison.ShowingLongerHistory')}
          </span>
        </Tooltip>
      )}
      {otherDisclaimers.map(disclaimerKey => (
        <DisclaimerTooltip key={disclaimerKey} showLabel disclaimer={disclaimers[disclaimerKey]} data-private data-dd-privacy="mask" tooltipPlace="right" />
      ))}
      {hasRollingBackfill && (
        <div key={`rolling-backfill-disclaimer-${item._id}`}>
          <button onClick={() => setShowModal(true)}>
            <Icon type="info" size={12} boxPad={24} />
            <FormattedMessage id="Disclaimers.ViewFullDisclaimer" />
          </button>
          <ModalV2 show={showModal} onClose={() => setShowModal(false)} title="Common.Disclaimer" body={modalBody} />
        </div>
      )}
    </div>
  );
};
